import intersectionBy from "lodash/intersectionBy";
import { createSelector } from "reselect";

import groups from "./groups";
import products from "./products";
import resourceTypes from "./resource-types";
import resources from "./resources";
import roles from "./roles";
import services from "./services";
import users from "./users";

const policies = {};

/**
 * Returns all policies
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.get = state => state.policies.items;

/**
 * Returns all policies except those attached to self-referential 'RBAC' service
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getFiltered = createSelector(
  [policies.get],
  policies =>
    policies.filter(policy => {
      if (policy.service && policy.service.$ref === "/api/rbac/v1/service/0") {
        return false;
      }
      return true;
    })
);

/**
 * Returns policies filtered by current product if set.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getByCurrentProduct = createSelector(
  [products.getCurrent, policies.getFiltered],
  (currentProduct, policies) => {
    if (!currentProduct) {
      return policies;
    }
    return policies.filter(policy => {
      if (!policy.product) {
        return false;
      }
      return policy.product.$ref === currentProduct.$uri;
    });
  }
);

/**
 * Returns policies filtered by current service if set.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getByCurrentService = createSelector(
  [services.getCurrent, policies.getFiltered],
  (currentService, policies) => {
    if (!currentService) {
      return policies;
    }
    return policies.filter(policy => {
      if (!policy.service) {
        return false;
      }
      return policy.service.$ref === currentService.$uri;
    });
  }
);

/**
 * Returns policies filtered by current resource if set.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getByCurrentResource = createSelector(
  [resources.getCurrent, policies.getFiltered],
  (currentResource, policies) => {
    if (!currentResource) {
      return policies;
    }
    return policies.filter(policy => {
      if (!policy.resource) {
        return false;
      }
      return policy.resource.$ref === currentResource.$uri;
    });
  }
);

/**
 * Returns policies filtered by current user if set.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getByCurrentUser = createSelector(
  [users.getCurrent, policies.getFiltered],
  (currentUser, policies) => {
    if (!currentUser) {
      return policies;
    }
    return policies.filter(policy => {
      if (!policy.user) {
        return false;
      }
      return policy.user.$ref === currentUser.$uri;
    });
  }
);

/**
 * Returns policies filtered by current group if set.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getByCurrentGroup = createSelector(
  [groups.getCurrent, policies.getFiltered],
  (currentGroup, policies) => {
    if (!currentGroup) {
      return policies;
    }
    return policies.filter(policy => {
      if (!policy.group) {
        return false;
      }
      return policy.group.$ref === currentGroup.$uri;
    });
  }
);

/**
 * Returns policies filtered by current resource if set,
 * otherwise return empty list
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getInScope = createSelector(
  [resources.getCurrent, policies.getByCurrentResource],
  (currentResource, policiesByResource) => {
    if (currentResource) {
      return policiesByResource;
    }
    return [];
  }
);

/**
 * Return dirty policies marked for update
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getForUpdate = createSelector(
  [policies.getFiltered],
  policies => policies.filter(policy => policy.requiresUpdate === true)
);

/**
 * Return dirty policies marked for delete
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getForDelete = createSelector(
  [policies.getFiltered],
  policies => policies.filter(policy => policy.requiresDelete === true)
);

/**
 * Returns policies with hydrated groups, users and role.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getWithDetails = createSelector(
  [
    policies.get,
    roles.get,
    groups.get,
    users.get,
    products.get,
    services.get,
    resources.get
  ],
  (policies, roles, groups, users, products, services, resources) => {
    const policiesWithDetails = policies.map(policy => {
      const details = { ...policy };
      details.role = roles.find(role => policy.role.$ref === role.$uri);
      if (policy.group) {
        details.group = groups.find(group => policy.group.$ref === group.$uri);
      }
      if (policy.user) {
        details.user = users.find(user => policy.user.$ref === user.$uri);
      }
      if (policy.product) {
        details.product = products.find(
          product => policy.product.$ref === product.$uri
        );
      }
      if (policy.service) {
        details.service = services.find(
          service => policy.service.$ref === service.$uri
        );
      }
      if (policy.resource) {
        details.resource = resources.find(
          resource => policy.resource.$ref === resource.$uri
        );
      }
      return details;
    });
    return policiesWithDetails;
  }
);

/**
 * Returns policies with hydrated groups, users, role, resourceType, service and product.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getWithFullDetails = createSelector(
  [policies.getWithDetails, resourceTypes.get, products.get, services.get],
  (policies, resourceTypes, products, services) => {
    const policiesWithFullDetails = policies.map(policy => {
      const details = { ...policy };
      if (policy.resource) {
        details.resourceType = resourceTypes.find(
          resourceType => policy.resource.type.$ref === resourceType.$uri
        );
        if (!policy.service) {
          details.service = services.find(
            service => policy.resource.service.$ref === service.$uri
          );
        }
      }
      if (details.service && !policy.product) {
        details.product = products.find(
          product => details.service.product.$ref === product.$uri
        );
      }
      return details;
    });
    return policiesWithFullDetails;
  }
);

/**
 * Returns policies filtered by current service as well as resources within that
 * service if set.
 * @param {Object} state
 * @returns {Number}
 */
policies.getAllByCurrentService = createSelector(
  [services.getCurrent, policies.getWithFullDetails],
  (currentService, policies) => {
    if (currentService) {
      const policiesInService = policies.filter(
        policy => policy.service && policy.service.$uri === currentService.$uri
      );
      return policiesInService;
    }
    return [];
  }
);

/**
 * Returns policies count filtered by resources in current service if set.
 * @param {Object} state
 * @returns {Number}
 */
policies.getCountByCurrentService = createSelector(
  [policies.getAllByCurrentService],
  policies => policies.length
);

/**
 * Returns policies count filtered by current user if set.
 * @param {Object} state
 * @returns {Number}
 */
policies.getCountByCurrentUser = createSelector(
  [policies.getByCurrentUser],
  policies => policies.length
);

/**
 * Returns policies count filtered by current group if set.
 * @param {Object} state
 * @returns {Number}
 */
policies.getCountByCurrentGroup = createSelector(
  [policies.getByCurrentGroup],
  policies => policies.length
);

/**
 * Returns policies marked for update with details.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getForUpdateDetails = createSelector(
  [policies.getForUpdate, policies.getWithFullDetails],
  (policiesForUpdate, policiesWithDetails) =>
    intersectionBy(policiesWithDetails, policiesForUpdate, "id")
);

/**
 * Returns policies marked for delete with details.
 * @param {Object} state
 * @returns {Object[]} policies
 */
policies.getForDeleteDetails = createSelector(
  [policies.getForDelete, policies.getWithFullDetails],
  (policiesForDelete, policiesWithDetails) =>
    intersectionBy(policiesWithDetails, policiesForDelete, "$uri")
);

/**
 * Returns if any dirty policies exist in the store.
 * @param {Object} state
 * @returns {boolean} policies
 */
policies.hasDirty = createSelector(
  [policies.getForUpdate, policies.getForDelete],
  (policiesForUpdate, policiesForDelete) =>
    policiesForUpdate.concat(policiesForDelete).length > 0
);

/**
 * Returns if policies errors exist.
 * @param {Object} state
 * @returns {boolean}
 */
policies.hasErrors = state =>
  Object.prototype.hasOwnProperty.call(state.policies, "error") || false;

export default policies;
